import React from "react";
import { bool, node, oneOf, shape, string } from "prop-types";
import { spacingPropTypes, spacingClassName, spacingProps } from "../props/spacing";
import Block from "../Block";
import { Col, Row } from "../Grid";
import Heading from "../Heading";
import { classes } from "../utils/classes";
import pureProps, { deepMergeProps } from "../utils/pureProps";
import { utilityClassName, utilityProps, utilityPropTypes } from "../props/utility";
import { sxClassName, sxProps, sxPropTypes } from "../props/sx";

const Label = ({ children, type = "default", spacing = { ...spacingProps }, ...props }) => (
	<Block
		className={classes(`feature__meta-label-${type}`, ...spacingClassName(spacing))}
		{...props}
	>
		{children}
	</Block>
);

Label.propTypes = {
	type: oneOf(["default", "without-line"]),
	spacing: spacingPropTypes,
	children: node.isRequired,
};

const Title = ({
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	children,
	className = "",
	underline,
	...props
}) => (
	<Heading
		Tag="h3"
		className={classes(
			"feature__meta-title",
			underline && `feature__meta-title--border-${underline}`,
			...utilityClassName(utility),
			...sxClassName(deepMergeProps({ fontSize: "l" }, pureProps(sx)), true),
			...spacingClassName(spacing),
			className,
		)}
		{...props}
	>
		{children}
	</Heading>
);

Title.propTypes = {
	underline: oneOf(["top-left", "top-right", "top-right--md"]),
	...Heading.propTypes,
};

const Description = ({ children, className, ...props }) => (
	<Block className={classes("feature__meta-description", className)} {...props}>
		{children}
	</Block>
);

Description.propTypes = {
	...Block.propTypes,
};

const Action = ({ children }) => <Block>{children}</Block>;

Action.propTypes = {
	children: node.isRequired,
};

const FeatureMeta = ({
	numberItem,
	children,
	sm = 12,
	md = 6,
	className = "",
	indent = true,
	numberProps = {},
	...props
}) => {
	const { sx = {}, utility = {}, spacing = {} } = numberProps;
	const blockClassName = numberProps?.className || null;
	return (
		<Col sm={sm} md={md} className={classes("feature__meta", className)} {...props}>
			<Block className={classes(indent && "feature__meta--indent")}>
				<Row>
					{numberItem ? (
						<Col shrink>
							<Block
								className={classes(
									"feature__meta-number",
									...utilityClassName(pureProps(utility)),
									...sxClassName(pureProps(sx)),
									...spacingClassName(pureProps(spacing)),
									blockClassName,
								)}
							>
								{numberItem}
							</Block>
						</Col>
					) : null}
					<Col auto>{children}</Col>
				</Row>
			</Block>
		</Col>
	);
};

FeatureMeta.propTypes = {
	numberItem: string,
	className: string,
	children: node.isRequired,
	sm: Col.propTypes.sm,
	md: Col.propTypes.md,
	indent: bool,
	numberProps: shape({
		sx: sxPropTypes,
		spacing: spacingPropTypes,
		utility: utilityPropTypes,
		className: string,
	}),
};

FeatureMeta.Label = Label;
FeatureMeta.Title = Title;
FeatureMeta.Description = Description;
FeatureMeta.Action = Action;

export default FeatureMeta;
